'use client';

import { usePathname } from 'next/navigation';
import type { selectedCartDateTime } from '@/app/(ordering)/shop/menu/data/shopdata.type';

import type {
	BasketResponse,
	BasketWantedTimesProps,
} from '@/app/(ordering)/rewards/data/data.type';
import { CartContext } from '@/app/(ordering)/shop/CartProvider/CartProvider';
import type { OrderDateTimeProps } from '@/app/(ordering)/shop/CartProvider/cartProviderTypes';
import { getDateName, getOrderDateTimeFromOloTimeWanted } from '@/utils/dateTimeUtils';
import { useContext, useEffect, useState } from 'react';
import styles from './dateTime.module.css';
import { useAppInsightsContext, useTrackEvent } from '@microsoft/applicationinsights-react-js';
import { Button } from '@/components/common/Button/Button';
import { BackArrow } from '@/components/common/Icons/BackArrow';
import { ErrorObj, parseError } from '@/utils/parseError';

type DateTimeComponentProps = {
	orderType: string;
	onCloseDateTime: () => void;
	hideArrow?: boolean;
};

const DateTimeComponent = ({ orderType, onCloseDateTime, hideArrow }: DateTimeComponentProps) => {
	const [times, setTimes] = useState(Array<string>);
	const [isdefault, setIsdefault] = useState(true);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isError, setIsError] = useState<ErrorObj | null>(null);
	const {
		apiCart,
		tempCart,
		setNewDateTime,
		address,
		validateAndRefetchCart,
		apiError,
		setApiError,
	} = useContext(CartContext);
	const appInsights = useAppInsightsContext();
	const pathname = usePathname();

	const getTrackNoAvailableTimesData = (date?: Date | undefined) => {
		if (!tempCart) {
			return undefined;
		}

		const storeTimeData = {
			storeNumber: tempCart.storeNumber,
			wantedTimes: tempCart.wantedTimes,
			dateSelected: date,
		};

		return storeTimeData;
	};

	const trackNoAvailableTimes = useTrackEvent(
		appInsights,
		'NO_AVAILABLE_TIMES',
		getTrackNoAvailableTimesData(),
		true
	);
	const [selectedDateTime, onSelectDateTime] = useState<OrderDateTimeProps>({
		orderDate: new Date(),
		orderTime: 'asap',
	});

	useEffect(() => {
		let cartDateTime: OrderDateTimeProps = address.orderDateTime;
		if (tempCart) {
			cartDateTime = getOrderDateTimeFromOloTimeWanted(tempCart);
		} else if (apiCart) {
			cartDateTime = getOrderDateTimeFromOloTimeWanted(apiCart);
		}
		onSelectDateTime(cartDateTime);

		if (isdefault && (tempCart?.wantedTimes || apiCart?.wantedTimes)) {
			const generatedTimes = genTimes(cartDateTime.orderDate);
			if (generatedTimes) {
				setTimes(generatedTimes);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tempCart, apiCart]);

	const handleTimeClick = (time: string) => {
		onSelectDateTime({
			orderDate: selectedDateTime.orderDate,
			orderTime: time,
		});
	};
	const handleDateClick = (date: Date) => {
		setIsdefault(false);
		const generatedTimes = genTimes(date, true);
		if (generatedTimes) {
			setTimes(generatedTimes);
			onSelectDateTime({
				orderDate: date,
				orderTime: generatedTimes[0],
			});
		}
	};

	const genTimes = (date: Date, clicked: boolean = false) => {
		const selectedDate = date.setHours(0, 0, 0, 0);
		const today = new Date().setHours(0, 0, 0, 0);
		const allowAsap = (tempCart && tempCart.allowAsap) || (apiCart && apiCart.allowAsap);

		if (cartTimeArray.length === 0) {
			if (allowAsap && selectedDate === today) {
				return ['asap'];
			}

			return [];
		}

		const availableOrderDate = cartTimeArray.find(
			(x) => x?.timeValue.setHours(0, 0, 0, 0) === selectedDate
		);

		// if selectedDate is not available, try the first available day instead
		if (!availableOrderDate && !clicked) {
			handleDateClick(cartTimeArray[0].timeValue);
			return undefined;
		}

		const orderTimes = availableOrderDate ? availableOrderDate.orderTimes : [];

		const newtimes: string[] = [];
		orderTimes?.forEach((element) => {
			const d = new Date(element);
			let hr = d.getHours();
			const mn = d.getMinutes();
			let sr = mn.toString();
			let tm: string;
			if (mn === 0) {
				sr = sr + '0';
			}
			if (hr >= 12) {
				if (hr !== 12) {
					hr = hr - 12;
				}
				tm = hr.toString() + ':' + sr + ' PM';
			} else {
				tm = hr.toString() + ':' + sr + ' AM';
			}
			if (tm !== '0:00 AM') {
				newtimes.push(tm);
			}
		});

		if (orderTimes?.length === 0 || newtimes?.length === 0) {
			if (appInsights) {
				trackNoAvailableTimes(getTrackNoAvailableTimesData(date));
			}
			return [];
		}

		const firstAvailableDay = new Date(orderTimes[0]).setHours(0, 0, 0, 0);
		return allowAsap && firstAvailableDay === today ? ['asap', ...newtimes] : newtimes;
	};

	const updateCartDateTime = async (selectedDateTime: OrderDateTimeProps) => {
		try {
			setIsLoading(true);
			setIsError(null);

			if (tempCart || apiCart) {
				await setNewDateTime(selectedDateTime);

				if (pathname === '/checkout' && apiCart) {
					await validateAndRefetchCart(apiCart?.id);
				}
			}
			setIsLoading(false);
			if (apiError) {
				setApiError(undefined);
			}

			onCloseDateTime();
		} catch (err) {
			setIsLoading(false);
			if (pathname === '/checkout') {
				setApiError(err as ErrorObj);
			} else {
				setIsError(err as ErrorObj);
			}
		}
	};

	const cartTimeArray: Array<selectedCartDateTime> = [];
	const timeValue = [];

	if ((tempCart && tempCart.wantedTimes) || (apiCart && apiCart.wantedTimes)) {
		let wantedTimes: BasketWantedTimesProps[] = [];

		if (tempCart && tempCart.wantedTimes) {
			wantedTimes = [...tempCart.wantedTimes];
		} else if (apiCart && apiCart.wantedTimes) {
			wantedTimes = [...apiCart.wantedTimes];
		}

		for (let j = 0; j <= wantedTimes.length - 1; j++) {
			const cartDateTime: selectedCartDateTime = {
				timeValue: new Date(),
				orderTimes: [],
			};
			timeValue.push(new Date(wantedTimes[j].timeValue));
			cartDateTime.timeValue = new Date(wantedTimes[j].timeValue);
			cartDateTime.orderTimes = wantedTimes[j].orderTimes ?? [];
			cartTimeArray.push(cartDateTime);
		}
	}
	const selectableDates = timeValue.length > 0 ? timeValue : [new Date()];

	const isItemSelected = (time: string) => {
		return selectedDateTime.orderTime.toLowerCase() === time.toLowerCase();
	};

	const formatTimeForDisplay = (time: string) => {
		return time === 'asap' ? 'As Soon as Possible' : time;
	};

	return (
		<>
			{!hideArrow && (
				<button aria-label="back" onClick={onCloseDateTime}>
					<BackArrow className={styles.backArrow} />
				</button>
			)}

			<h3
				className={`${styles.title} order-title text-center uppercase`}
				data-type-style="subtitle-2"
				data-ink="medium"
			>
				{orderType} TIME
			</h3>

			{apiCart?.appliedRewards.length ? <ExpirationMessage apiCart={apiCart} /> : ''}
			{isError && (
				<p
					data-type-style="body-1"
					className="text-center text-sm font-bold uppercase text-[#c8102e]"
				>
					{parseError(isError.message)}
				</p>
			)}
			<div className="mb-2 font-black" data-type-style="label" data-ink="base">
				SELECT DAY
			</div>
			<div className="lock overflow-x-scroll">
				<div className="grid w-max auto-cols-[minmax(125px,_1fr)] grid-flow-col gap-2 pb-4">
					{selectableDates.map((selectableDate, index) => (
						<Button
							key={index}
							className={styles.dateTimeButton}
							variant="secondary"
							data-type-style="button-web"
							onClick={() => {
								handleDateClick(selectableDate);
							}}
							style={{
								backgroundColor:
									new Date(selectedDateTime.orderDate).toDateString() ===
									selectableDate.toDateString()
										? 'var(--color-green-300)'
										: 'white',
								color:
									new Date(selectedDateTime.orderDate).toDateString() ===
									selectableDate.toDateString()
										? 'white'
										: '#54585A',
							}}
						>
							{getDateName(selectableDate)}
							<div
								style={{
									color:
										new Date(selectedDateTime.orderDate).toDateString() ===
										selectableDate.toDateString()
											? 'white'
											: '#54585A',
								}}
								data-ink="light"
								className={styles.monthLabel}
							>
								{selectableDate.toLocaleString('default', { month: 'long' })}{' '}
								{selectableDate.getDate()}
							</div>
						</Button>
					))}
				</div>
			</div>
			<div className="mb-2 mt-8 font-black" data-type-style="label">
				{times?.length > (getDateName(selectedDateTime.orderDate) === 'Today' ? 1 : 0)
					? 'SELECT TIME'
					: 'NO AVAILABLE TIMES FOR THIS DAY'}
			</div>
			<div className="grid max-h-[calc(100vh_-_35rem)] grid-cols-1 gap-0 overflow-y-scroll sm:max-h-[400px]">
				{times.map((time, index) => (
					<Button
						key={index}
						data-type-style="button-web"
						className={`${styles.timeButton} flex flex-row items-center justify-between`}
						variant="text"
						onClick={() => handleTimeClick(time.toLowerCase())}
					>
						<div>{formatTimeForDisplay(time)}</div>
						<div className={isItemSelected(time) ? styles.checked : styles.unchecked} />
					</Button>
				))}
			</div>
			<div className="flow mt-8">
				<div className="mx-auto grid w-full max-w-xs grid-cols-1 gap-6">
					<Button
						variant="tertiary"
						data-size="large"
						data-type-style="button-mobile"
						className={styles.schedulebutton}
						isLoading={isLoading}
						isLoadingFill={'gray'}
						disabled={times?.length === 0 || isLoading}
						onClick={async () => await updateCartDateTime(selectedDateTime)}
					>
						Schedule {orderType} Time
					</Button>
				</div>
			</div>
		</>
	);
};

export default DateTimeComponent;

// ExpirationMessage Component to display message.
interface ExpirationMessageProps {
	apiCart: BasketResponse;
}

const ExpirationMessage: React.FC<{ apiCart: BasketResponse }> = ({
	apiCart,
}: ExpirationMessageProps) => {
	const getFormattedExpirationMessage = (apiCart: BasketResponse): string | null => {
		if (!apiCart?.appliedRewards?.length) {
			return null;
		}

		const expirationDate = apiCart.appliedRewards[0]?.expirationdate?.trim() || '';
		if (!expirationDate) {
			return null;
		}
		function getValidDate(dateString: string): Date | null {
			const regex = /^(\d{4})(\d{2})(\d{2})/;
			const match = dateString.match(regex);
			if (match) {
				const formattedDate = `${match[1]}-${match[2]}-${match[3]}`;
				return new Date(formattedDate);
			}
			return null;
		}
		const validDate = getValidDate(expirationDate);
		if (!validDate) {
			return null;
		}
		const formattedDate: string = validDate.toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
			timeZone: 'UTC',
		});

		return `You have a reward or offer applied to your order that expires on ${formattedDate}. Please select a date prior to the expiration to apply the reward or offer to your order.`;
	};

	const message = getFormattedExpirationMessage(apiCart);

	return (
		<>
			{message && (
				<p data-type-style="label" className="mb-8 text-lg">
					{message}
				</p>
			)}
		</>
	);
};
